import { useEffect } from "react";

let locks = 0;
let saved = null;

// Exact scroll lock: body pinned at the current offset, restored to the same pixel on release (nested overlays counted).
function lock() {
  if (locks++ > 0) return;
  const y = window.scrollY;
  const b = document.body.style;
  saved = { y, position: b.position, top: b.top, width: b.width, overflow: b.overflow };
  Object.assign(b, { position: "fixed", top: `-${y}px`, width: "100%", overflow: "hidden" });
}

function unlock() {
  if (--locks > 0 || !saved) return;
  const { y, ...rest } = saved;
  Object.assign(document.body.style, rest);
  saved = null;
  window.scrollTo(0, y);
}

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Overlay behaviour: scroll lock, Escape, focus trap inside `ref`, focus restored on close.
export function useOverlay(open, onClose, ref) {
  useEffect(() => {
    if (!open) return undefined;
    const before = document.activeElement;
    lock();
    const t = window.setTimeout(() => {
      const el = ref.current;
      if (el && !el.contains(document.activeElement)) (el.querySelector("[data-autofocus]") || el.querySelector(FOCUSABLE) || el).focus?.();
    }, 0);
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== "Tab" || !ref.current) return;
      const items = [...ref.current.querySelectorAll(FOCUSABLE)].filter((x) => x.tabIndex >= 0 && !x.closest("[hidden]"));
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      unlock();
      before?.focus?.({ preventScroll: true });
    };
  }, [open, onClose, ref]);
}
